import { useState, useEffect } from 'react';
import { useDefaultRestaurant } from './useRestaurantEta';
import { estaDisponible } from '../utils/disponibilidad';

const API_URL = import.meta.env.VITE_API_URL;

// Id del restaurante único, conocido desde el arranque: con él el menú se pide
// en paralelo con el restaurante, en vez de esperar a que este llegue.
const ID_RESTAURANTE = import.meta.env.VITE_DEFAULT_BUSINESS_ID;

// Copia local del menú ya procesado (stale-while-revalidate): desde la segunda
// apertura el home se pinta al instante con ella y se actualiza en silencio
// cuando llega la versión nueva.
const MENU_KEY = 'menu_v1';

const leerMenuGuardado = () => {
  try { return JSON.parse(localStorage.getItem(MENU_KEY)) || null; } catch { return null; }
};

// Populares = productos del menú marcados isPopular (sin fetch extra). Es una
// vitrina: un producto que no se puede pedir no va ahí.
const sacarPopulares = (categorias) =>
  categorias.flatMap((c) => c.products).filter((p) => p.isPopular && estaDisponible(p));

const procesar = (raw) => raw
  .map((cat) => ({
    _id: cat._id,
    name: cat.name,
    image: cat.image || null,
    icon: cat.icon || null,
    // false = sale en el menú pero no en la barra de categorías.
    showInCategoryBar: cat.showInCategoryBar !== false,
    products: (cat.products || []).map((p) => ({ ...p, id: p._id || p.id })),
  }))
  .filter((cat) => cat.products.length > 0);

/**
 * Single-restaurant: carga el menú del restaurante único — categorías de
 * producto con sus productos (solo las que tienen productos). Es la data que
 * alimenta los chips de categoría y las secciones de productos del home.
 *
 * `loading` solo es true cuando no hay nada que mostrar todavía (primera
 * apertura sin copia guardada); con copia, el menú se ve de inmediato.
 */
export function useRestaurantMenu() {
  const restaurant = useDefaultRestaurant();
  const businessId = ID_RESTAURANTE || restaurant?._id || restaurant?.id;
  const [categories, setCategories] = useState(() => leerMenuGuardado() || []);
  const [popularProducts, setPopularProducts] = useState(() => sacarPopulares(leerMenuGuardado() || []));
  const [loading, setLoading] = useState(() => !leerMenuGuardado());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    let alive = true;
    setError(null);
    // includeUnavailable: el menú pinta también los no disponibles (atenuados,
    // con la etiqueta "Vuelve pronto"). Las secciones curadas los descartan.
    fetch(`${API_URL}/api/product-categories/business/${businessId}?populate=products&includeUnavailable=true`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Error al cargar el menú'))))
      .then((data) => {
        if (!alive) return;
        const processed = procesar(data.categories || data.response || data.data || []);
        setCategories(processed);
        setPopularProducts(sacarPopulares(processed));
        try { localStorage.setItem(MENU_KEY, JSON.stringify(processed)); } catch { /* sin storage */ }
      })
      // Con copia guardada, un fallo de red no rompe el home: se sigue viendo
      // la última versión. Solo sin copia se muestra el error.
      .catch((err) => { if (alive && !leerMenuGuardado()) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [businessId]);

  return { categories, popularProducts, loading, error };
}
